import React, { useRef, useEffect } from 'react';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/light.css';
import './InputDatePicker.scss';
import flatpickr from 'flatpickr';
import { InputDatePickerProps } from './types';
import CustomDateInput from './CustomDateInput';
import { useTranslation } from 'react-i18next';
import {
    getLocalizationConfig,
    stringArrayToDateString,
    dateStringToStringArray,
    DefaultMaxDate,
    DefaultMinDate,
} from './utils';

const InputDatePicker = ({
    allowInput = true,
    ...props
}: InputDatePickerProps) => {
    const { t } = useTranslation()
    flatpickr.localize(getLocalizationConfig())
    const FlatPickerComponent = Flatpickr as any
    const pickerRef = useRef<any>(null)

    const updateValueFromOutside = () => {
        const pickerElement =
            pickerRef && pickerRef.current && pickerRef.current.flatpickr
        if (pickerElement) {
            const newValue = stringArrayToDateString(props.value)
            pickerElement.setDate(newValue || '')
        }
    }

    useEffect(() => {
        // Set initial value
        updateValueFromOutside()
    }, [])

    useEffect(() => {
        // If value is updated from outside the datepicker
        updateValueFromOutside()
    }, [props.value])

    const onChangeDate = (selectedDates: any[], dateStr: string) => {
        const dateArray = dateStringToStringArray(dateStr)
        props.onChange(dateArray)
    }

    const onOpenCalendar = () => {
        const pickerElement =
            pickerRef && pickerRef.current && pickerRef.current.flatpickr
        if (pickerElement) {
            pickerElement.open()
            // Set focus on first date in calendar, for keyboard usage.
            const firstDayElement = document.querySelectorAll(
                '.flatpickr-day:not(.prevMonthDay)'
            )[0] as HTMLElement
            firstDayElement && firstDayElement.focus()
        }
    }

    const onClose = (selectedDates: any[], dateStr: string, instance: any) => {
        instance.setDate(dateStr, true)
    }

    const onCloseCalendar = () => {
        const pickerElement =
            pickerRef && pickerRef.current && pickerRef.current.flatpickr
        if (pickerElement) {
            pickerElement.close()
        }
    }

    const options = {
        allowInput: allowInput,
        altInput: true,
        altFormat: t('global.dateFormat'),
        clickOpens: true,
        closeOnSelect: true,
        disableMobile: true,
        maxDate: props.maxDate || DefaultMaxDate,
        minDate: props.minDate || DefaultMinDate,
        mode: props.mode,
        nextArrow: '',
        onReady: (a: any, b: any, fp: any) => {
            fp &&
                fp.altInput &&
                fp.altInput.setAttribute('aria-label', props.label)
        },
        prevArrow: '',
    }

    return (
        <div className={props.className}>
            <FlatPickerComponent
                className="date-input-field"
                options={options}
                onChange={onChangeDate}
                onClose={onClose}
                ref={pickerRef}
                render={({ value, ...inputProps }: any, ref: any) => {
                    return (
                        <CustomDateInput
                            value={value}
                            disabled={!!props.disabled}
                            inputRef={ref}
                            hasError={!!props.hasError}
                            datepickerProps={props}
                            onOpenCalendar={onOpenCalendar}
                            onCloseCalendar={onCloseCalendar}
                            {...inputProps}
                        />
                    )
                }}
            />

            {props.hasError && props.errorText && (
                <div className="alert alert--danger alert--input">
                    {props.errorText}
                </div>
            )}
        </div>
    )
}

export default InputDatePicker
